<script setup lang="ts">
const props = defineProps({
  size: {
    type: String,
    validator: (value: string) => {
      return ["custom", "sm", "md", "lg", "xl", "2xl", "3xl"].includes(value);
    },
    default: undefined,
  },
  customClass: {
    type: String,
    required: false,
    default: undefined,
  },
  profilePicture: {
    type: String,
    required: false,
    default: "",
  },
  id: {
    type: String,
    required: false,
    default: "",
  },
  specialBorder: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { getUserProfile } = useUserController();
const userStore = useUserStore();

const imageShown = computed(() => {
  if (props.profilePicture) return props.profilePicture;
  if (!props.id) return null;
  if (props.id === userStore.id) return userStore.profilePicture;
  let userProfile = useUserCache().getUserProfileFromCache(props.id);
  if (userProfile) return userProfile.profilePicture;
  return fetchedPfp.value;
});

const extraClasses = computed(() => {
  switch (props.size) {
    case "custom":
      return props.customClass ? props.customClass : "";
    case "sm":
      return "w-6 h-6 ";
    case "md":
      return "w-12 h-12 ";
    case "lg":
      return "w-16 h-16 ";
    case "xl":
      return "p-2 w-20 h-20 ";
    case "2xl":
      return "p-1 w-24 h-24 ";
    case "3xl":
      return "p-1 w-32 h-32 ";
    default:
      return "w-40 h-40 p-1.5";
  }
});

const specialBorderColor = computed(() => {
  return props.specialBorder ? "border-[#FFB800]" : "border-secondary-500";
});

const routingToProfile = (id: string) => {
  if (!id) {
    return "";
  } else if (id === userStore.id) {
    return `/collection/${useAppStateStore().currentMerchant}`;
  } else if (id) {
    return `/user/${id}`;
  } else {
    return "";
  }
};

const { data: fetchedPfp, execute: fetchPfp } = await useAsyncData(
  `profilePicture/${props.id}`,
  async () => {
    try {
      const { payload } = await getUserProfile(props.id);
      if (!payload) return null;
      return payload.profilePicture;
    } catch (error) {
      console.error("Error fetching profile picture", error);
      return null;
    }
  },
  {
    default: () => "",
    dedupe: "defer",
    immediate: false,
  }
);

const init = async () => {
  if (props.profilePicture) return;
  if (!props.id) return;
  await fetchPfp();
};

await init();
</script>

<template>
  <NuxtLinkLocale :to="routingToProfile(id)">
    <img
      :src="getAvatar(imageShown)"
      :alt="$t('main.imageAltTexts.CommonUserProfilePicture')"
      :class="`${extraClasses} ${
        profilePicture !== 'GFAL'
          ? `border-2 ${specialBorderColor}  rounded-full`
          : 'p-1'
      } transition-all transform duration-700 ease-in-out`" />
  </NuxtLinkLocale>
</template>
